import {
  useMarkAllAsRead,
  useNotifications,
  useUnreadCount,
  useUpdateReadStatus,
} from '@/hooks/useNotifications';
import { useNotificationsStore } from '@/store/notifications.store';
import { Notification } from '@/types/notification.types';
import { cn } from '@/utils/cn';
import { format } from '@/utils/format';
import {
  AlertCircle,
  Bell,
  Check,
  CheckCheck,
  Loader2,
  Mail,
  X,
} from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// Notification Item Component
const NotificationItem = ({ notification }: { notification: Notification }) => {
  const { t } = useTranslation();
  const { mutate: updateReadStatus } = useUpdateReadStatus();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <Check className="text-success-600 h-4 w-4" />;
      case 'failed':
        return <AlertCircle className="text-error-600 h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4 text-neutral-600" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'database':
        return <Bell className="h-3 w-3" />;
      case 'email':
        return <Mail className="h-3 w-3" />;
      default:
        return <Bell className="h-3 w-3" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'text-success-700 bg-success-50 border-success-200';
      case 'failed':
        return 'text-error-700 bg-error-50 border-red-200';
      default:
        return 'text-neutral-700 bg-neutral-50 border-neutral-200';
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    updateReadStatus({
      notificationId: notification.uuid,
      data: { isRead: !notification.isRead },
    });
  };

  const getActionText = (templateId: string) => {
    switch (templateId) {
      case 'calculate-dsn':
        return t('notifications.actions.calculateDsn', 'Calcul DSN');
      case 'generate-bulletin':
        return t(
          'notifications.actions.generateBulletin',
          'Génération de bulletins'
        );
      default:
        return templateId;
    }
  };

  return (
    <div
      className={cn(
        'group hover:bg-neutral-25 relative border-l-2 p-3 transition-all',
        notification.isRead
          ? 'border-l-neutral-200'
          : 'border-l-primary-500 bg-primary-25'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Status Icon */}
        <div className="flex-shrink-0">
          {getStatusIcon(notification.status)}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-neutral-900">
                {getActionText(notification.templateId)}
              </p>
              <p className="mt-1 line-clamp-2 text-xs text-neutral-600">
                {notification.subject}
              </p>
            </div>

            {/* Type & Read Status */}
            <div className="flex flex-shrink-0 items-center gap-2">
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
                  getStatusColor(notification.status)
                )}
              >
                {getTypeIcon(notification.type)}
                {t(`notifications.types.${notification.type}`)}
              </span>

              <button
                onClick={handleMarkAsRead}
                className={cn(
                  'rounded-lg p-1 transition-colors hover:bg-neutral-100',
                  notification.isRead ? 'text-neutral-400' : 'text-primary-600'
                )}
                title={
                  notification.isRead
                    ? t('notifications.markAsUnread', 'Mark as unread')
                    : t('notifications.markAsRead', 'Mark as read')
                }
              >
                <div
                  className={cn(
                    'h-2 w-2 rounded-full',
                    notification.isRead ? 'bg-neutral-300' : 'bg-primary-600'
                  )}
                />
              </button>
            </div>
          </div>

          {/* Timestamp */}
          <p className="mt-2 text-xs text-neutral-500">
            {format.dateTime(notification.createdAt)}
          </p>
        </div>
      </div>
    </div>
  );
};

// Main Popover Component
export const NotificationPopover = () => {
  const { t } = useTranslation();
  const popoverRef = useRef<HTMLDivElement>(null);

  const {
    isPopoverOpen,
    setPopoverOpen,
    limit,
    setReadFilter,
    setStatusFilter,
  } = useNotificationsStore();
  const { notifications, isLoading } = useNotifications();
  const { unreadCount } = useUnreadCount();
  const { mutate: markAllAsRead, isPending: isMarkingAllAsRead } =
    useMarkAllAsRead();

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setPopoverOpen(false);
      }
    };

    if (isPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopoverOpen, setPopoverOpen]);

  // Reset filter when opening popover
  useEffect(() => {
    if (isPopoverOpen) {
      setStatusFilter('failed');
      setReadFilter('all');
    }
  }, [isPopoverOpen, setReadFilter, setStatusFilter]);

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  if (!isPopoverOpen) return null;

  return (
    <div className="absolute top-full right-0 z-50 mt-2 w-[min(420px,calc(100vw-2rem))]">
      <div
        ref={popoverRef}
        className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-lg"
      >
        {/* Header */}
        <div className="border-b border-neutral-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-neutral-700" />
              <h3 className="font-semibold text-neutral-900">
                {t('notifications.title', 'Notifications')}
              </h3>
              {unreadCount > 0 && (
                <span className="bg-primary-100 text-primary-800 inline-flex items-center rounded-full px-2 py-1 text-xs font-medium">
                  {unreadCount}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={isMarkingAllAsRead}
                  className="inline-flex items-center gap-1 rounded-lg bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-200 disabled:opacity-50"
                >
                  {isMarkingAllAsRead ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <CheckCheck className="h-3 w-3" />
                  )}
                  {t(
                    'notifications.markAllAsRead',
                    'Tout marquer comme traité'
                  )}
                </button>
              )}

              <button
                onClick={() => setPopoverOpen(false)}
                className="rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
              <span className="ml-2 text-sm text-neutral-600">
                {t('notifications.loading', 'Chargement des alertes...')}
              </span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="mx-auto h-8 w-8 text-neutral-300" />
              <h4 className="mt-2 text-sm font-medium text-neutral-900">
                {t('notifications.empty.title', 'Aucune action requise')}
              </h4>
              <p className="mt-1 text-xs text-neutral-500">
                {t('notifications.empty.description', 'Tout est en ordre.')}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {notifications.slice(0, limit).map(notification => (
                <NotificationItem
                  key={notification.uuid}
                  notification={notification}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="border-t border-neutral-200 p-3">
            <Link
              to="/notifications"
              onClick={() => setPopoverOpen(false)}
              className="block w-full rounded-lg bg-neutral-50 py-2 text-center text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100"
            >
              {t('notifications.viewAll', 'Voir les alertes')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
